import { useState, useEffect, FormEvent } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SEOHead from '../../components/feature/SEOHead';

const SUBJECTS = [
  'General Enquiry',
  'Story Submission Help',
  'Account or Login Issue',
  'Moderation or Report',
  'Partnership or Press',
  'Bug or Technical Issue',
  'Other',
];

type Status = 'idle' | 'sending' | 'success' | 'error';

const ContactPage = () => {
  const [scrolled, setScrolled] = useState(false);
  const [status, setStatus] = useState<Status>('idle');
  const [charCount, setCharCount] = useState(0);
  const [form, setForm] = useState({
    name: '',
    email: '',
    subject: SUBJECTS[0],
    message: '',
  });

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    if (name === 'message') {
      if (value.length > 500) return;
      setCharCount(value.length);
    }
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (form.message.length > 500) return;
    setStatus('sending');

    const body = new URLSearchParams({
      name: form.name,
      email: form.email,
      subject: form.subject,
      message: form.message,
    });

    try {
      const res = await fetch('https://readdy.ai/api/form/d6srq0r4rjv84lqbqjn0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      if (res.ok) {
        setStatus('success');
        setForm({ name: '', email: '', subject: SUBJECTS[0], message: '' });
        setCharCount(0);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <SEOHead
        title="Contact Sinister Files | Horror Fiction Community Support"
        description="Get in touch with the Sinister Files team. Whether you have a question about your account, a story submission, or a press enquiry — we&apos;re here in the dark."
        canonical="https://sinisterfiles.com/contact"
      />
      <Navbar scrolled={scrolled} />

      {/* Hero */}
      <section className="relative min-h-[50vh] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage:
              'url(https://public.readdy.ai/ai/img_res/64f693cb8e187032f41e58b5ef796ce1.jpg)',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/65 via-black/55 to-[#0a0a0a]" />
        <div className="relative z-10 text-center px-4 max-w-3xl mx-auto w-full">
          <p className="text-red-500 text-sm font-['Cinzel'] tracking-[0.3em] uppercase mb-4">
            Send Word to the Shadows
          </p>
          <h1 className="text-5xl md:text-7xl font-bold text-white font-['Cinzel'] mb-6 leading-tight">
            Contact <span className="text-red-600">Us</span>
          </h1>
          <p className="text-gray-300 text-lg font-['Crimson_Text'] italic leading-relaxed">
            We read every message. Even the ones sent at 3am.
          </p>
        </div>
      </section>

      {/* Main */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-start">

            {/* Left Info */}
            <div className="lg:col-span-2 space-y-8">
              <div>
                <p className="text-red-500 text-xs font-['Cinzel'] tracking-[0.25em] uppercase mb-3">
                  Reach Out
                </p>
                <h2 className="text-3xl font-bold text-white font-['Cinzel'] mb-4">
                  We&apos;re Listening
                </h2>
                <div className="w-10 h-0.5 bg-red-600 mb-6" />
                <p className="text-gray-400 font-['Crimson_Text'] text-lg leading-relaxed">
                  Whether you need help with your account, want to report something, or just have a question about the platform, fill in the form and we&apos;ll get back to you as soon as we surface from the dark.
                </p>
              </div>

              <div className="space-y-5">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-red-600/10 rounded-lg flex-shrink-0">
                    <i className="ri-mail-line text-red-500 text-lg" />
                  </div>
                  <div>
                    <p className="text-white font-['Cinzel'] font-bold text-sm mb-1">Email</p>
                    <a
                      href="mailto:topher@sinisterfiles.com"
                      className="text-gray-400 hover:text-red-400 font-['Crimson_Text'] text-base transition-colors cursor-pointer"
                    >
                      topher@sinisterfiles.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-red-600/10 rounded-lg flex-shrink-0">
                    <i className="ri-time-line text-red-500 text-lg" />
                  </div>
                  <div>
                    <p className="text-white font-['Cinzel'] font-bold text-sm mb-1">Response Time</p>
                    <p className="text-gray-400 font-['Crimson_Text'] text-base">
                      Usually within 2–3 business days. Moderation reports are prioritised.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-red-600/10 rounded-lg flex-shrink-0">
                    <i className="ri-discuss-line text-red-500 text-lg" />
                  </div>
                  <div>
                    <p className="text-white font-['Cinzel'] font-bold text-sm mb-1">Community Forum</p>
                    <p className="text-gray-400 font-['Crimson_Text'] text-base">
                      For general discussion, head to the{' '}
                      <a
                        href="/community"
                        className="text-red-500 hover:text-red-400 transition-colors cursor-pointer"
                      >
                        Dark Congregation
                      </a>
                      .
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-[#0d0d0d] border border-red-900/20 rounded-lg p-5">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-information-line text-red-400 text-base" />
                  </div>
                  <p className="text-gray-400 font-['Crimson_Text'] text-sm leading-relaxed">
                    For urgent moderation concerns such as harmful content or harassment, please use the in-platform report button on the relevant story, comment, or post for fastest response.
                  </p>
                </div>
              </div>
            </div>

            {/* Form */}
            <div className="lg:col-span-3">
              <div className="bg-[#0d0d0d] border border-red-900/20 rounded-lg p-8">
                {status === 'success' ? (
                  <div className="flex flex-col items-center justify-center py-16 text-center">
                    <div className="w-16 h-16 flex items-center justify-center bg-red-600/10 rounded-full mb-6">
                      <i className="ri-check-line text-red-500 text-3xl" />
                    </div>
                    <h3 className="text-white font-['Cinzel'] font-bold text-2xl mb-3">
                      Message Received
                    </h3>
                    <p className="text-gray-400 font-['Crimson_Text'] text-lg leading-relaxed max-w-sm">
                      Your message has been sent. We&apos;ll be in touch within 2–3 business days.
                    </p>
                    <button
                      onClick={() => setStatus('idle')}
                      className="mt-8 px-6 py-2.5 border border-red-600/50 hover:border-red-600 text-red-400 hover:text-red-300 font-['Cinzel'] text-sm font-bold rounded-md transition-all cursor-pointer whitespace-nowrap"
                    >
                      Send Another
                    </button>
                  </div>
                ) : (
                  <form
                    data-readdy-form
                    id="contact-us-form"
                    onSubmit={handleSubmit}
                    className="space-y-6"
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-gray-300 font-['Cinzel'] text-xs tracking-widest uppercase mb-2">
                          Your Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="name"
                          required
                          value={form.name}
                          onChange={handleChange}
                          placeholder="e.g. Stephen King"
                          className="w-full bg-[#111] border border-red-900/30 focus:border-red-600/60 rounded-md px-4 py-3 text-gray-200 font-['Crimson_Text'] text-sm placeholder-gray-600 outline-none transition-colors"
                        />
                      </div>
                      <div>
                        <label className="block text-gray-300 font-['Cinzel'] text-xs tracking-widest uppercase mb-2">
                          Email Address <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="email"
                          name="email"
                          required
                          value={form.email}
                          onChange={handleChange}
                          placeholder="you@example.com"
                          className="w-full bg-[#111] border border-red-900/30 focus:border-red-600/60 rounded-md px-4 py-3 text-gray-200 font-['Crimson_Text'] text-sm placeholder-gray-600 outline-none transition-colors"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-gray-300 font-['Cinzel'] text-xs tracking-widest uppercase mb-2">
                        Subject <span className="text-red-500">*</span>
                      </label>
                      <select
                        name="subject"
                        required
                        value={form.subject}
                        onChange={handleChange}
                        className="w-full bg-[#111] border border-red-900/30 focus:border-red-600/60 rounded-md px-4 py-3 text-gray-200 font-['Crimson_Text'] text-sm outline-none transition-colors cursor-pointer"
                      >
                        {SUBJECTS.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-gray-300 font-['Cinzel'] text-xs tracking-widest uppercase mb-2">
                        Message <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        name="message"
                        required
                        rows={6}
                        value={form.message}
                        onChange={handleChange}
                        maxLength={500}
                        placeholder="Tell us what's on your mind..."
                        className="w-full bg-[#111] border border-red-900/30 focus:border-red-600/60 rounded-md px-4 py-3 text-gray-200 font-['Crimson_Text'] text-sm placeholder-gray-600 outline-none transition-colors resize-none"
                      />
                      <div className="flex justify-end mt-1">
                        <span className={`font-['Crimson_Text'] text-xs ${charCount >= 480 ? 'text-red-400' : 'text-gray-600'}`}>
                          {charCount}/500
                        </span>
                      </div>
                    </div>

                    {status === 'error' && (
                      <div className="flex items-center gap-3 bg-red-950/30 border border-red-900/40 rounded-md px-4 py-3">
                        <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                          <i className="ri-error-warning-line text-red-400 text-base" />
                        </div>
                        <p className="text-red-300 font-['Crimson_Text'] text-sm">
                          Something went wrong. Please try again or email us directly.
                        </p>
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={status === 'sending'}
                      className="w-full flex items-center justify-center gap-3 px-8 py-3.5 bg-red-600 hover:bg-red-700 disabled:opacity-60 disabled:cursor-not-allowed text-white font-['Cinzel'] font-bold rounded-md transition-all duration-300 cursor-pointer whitespace-nowrap"
                    >
                      {status === 'sending' ? (
                        <>
                          <i className="ri-loader-4-line animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <i className="ri-send-plane-line" />
                          Send Message
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
